"use client"

import * as React from "react"
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"
import LucideChevronLeft from "@/icons/component/LucideChevronLeft"
import LucideChevronRight from "@/icons/component/LucideChevronRight"
import LucideChevronDown from "@/icons/component/LucideChevronDown"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  hideNavigation,
  formatters,
  components,
  locale,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
  locale?: string
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      hideNavigation={hideNavigation}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale || undefined, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-7 select-none rounded-md p-0 aria-disabled:opacity-50 hover:bg-accent transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none bg-transparent border-0",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-7 select-none rounded-md p-0 aria-disabled:opacity-50 hover:bg-accent transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none bg-transparent border-0",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-md bg-background border border-border hover:border-primary/50 has-focus:border-2 has-focus:border-primary has-focus:outline-none transition-colors duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none px-3 py-2 h-auto",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "bg-background absolute inset-0 opacity-0 appearance-none rounded-md px-3 py-2 text-sm font-medium outline-none cursor-pointer transition-colors duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none text-foreground",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none text-foreground bg-background type-label-l",
          captionLayout === "label"
            ? ""
            : "[&>svg]:text-action flex h-auto items-center gap-1 rounded-md px-2 py-1 [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-on-surface-variant flex-1 select-none rounded-md type-label-s",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-[--cell-size] select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-full [&:last-child[data-selected=true]_button]:rounded-r-full",
          defaultClassNames.day
        ),
        range_start: cn(
          "bg-accent/60 rounded-l-full",
          defaultClassNames.range_start
        ),
        range_middle: cn(
          "bg-accent/60 rounded-none",
          defaultClassNames.range_middle
        ),
        range_end: cn(
          "bg-accent/60 rounded-r-full",
          defaultClassNames.range_end
        ),
        today: cn(
          "bg-accent text-accent-foreground rounded-full",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <LucideChevronLeft
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          if (orientation === "right") {
            return (
              <LucideChevronRight
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          return (
            <LucideChevronDown
              className={cn("size-4", className)}
              {...props}
            />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "relative flex size-8 aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 type-label-l leading-none transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none rounded-full",
        // Drop the default offset ring/border/shadow, then restore a proper
        // keyboard focus indicator: an inset 2px ring that hugs the circular
        // day button. A calendar grid with NO visible focus state fails
        // WCAG 2.4.7 — keyboard users couldn't see which day was focused.
        "focus:outline-none focus:ring-offset-0 focus:border-0 focus:shadow-none focus-visible:border-0 focus-visible:shadow-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        "aria-selected:outline-none aria-selected:border-0 aria-selected:shadow-none",
        // Hover state
        "hover:bg-accent hover:text-accent-foreground",
        // Single selection - filled circle, primary background, primary-foreground text
        "data-[selected-single=true]:bg-primary! data-[selected-single=true]:text-primary-foreground! data-[selected-single=true]:font-medium data-[selected-single=true]:rounded-full! data-[selected-single=true]:hover:bg-primary/90!",
        "data-[selected-single=true]:border-0! data-[selected-single=true]:shadow-none! data-[selected-single=true]:outline-none! data-[selected-single=true]:ring-0!",
        "data-[selected-single=true]:hover:text-primary-foreground!",
        // Range middle - slim accent line, no rounding
        "data-[range-middle=true]:bg-accent/60! data-[range-middle=true]:text-accent-foreground! data-[range-middle=true]:rounded-none!",
        // Range start/end - filled circle endpoints
        "data-[range-start=true]:bg-primary! data-[range-start=true]:text-primary-foreground! data-[range-start=true]:font-medium data-[range-start=true]:rounded-full! data-[range-start=true]:hover:bg-primary/90!",
        "data-[range-end=true]:bg-primary! data-[range-end=true]:text-primary-foreground! data-[range-end=true]:font-medium data-[range-end=true]:rounded-full! data-[range-end=true]:hover:bg-primary/90!",
        // Drop the default outline/border/shadow but keep the focus-visible
        // ring set above — the keyboard focus indicator must survive.
        "focus-visible:outline-none! focus-visible:border-0! focus-visible:shadow-none!",
        "group-data-[focused=true]/day:outline-none! group-data-[focused=true]/day:border-0! group-data-[focused=true]/day:shadow-none!",
        // Additional styles
        "[&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
